import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Check, GripVertical } from "lucide-react";

/**
 * MicroDemo - Animated demonstration of the app flow
 * 
 * Shows: Add task → Drag/Click to Done → "Back on track" confirmation
 * Auto-plays on scroll, loops gently, muted, calm aesthetic
 */
export const MicroDemo = () => {
  const [step, setStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  // Animation steps:
  // 0: Empty board
  // 1: Typing task
  // 2: Task added to "To Do"
  // 3: Task moving to "Done"
  // 4: Task in "Done" with checkmark
  // 5: "Back on track" message
  // 6: Reset to beginning

  const taskText = "finish pricing page";
  const [typedText, setTypedText] = useState("");

  // Intersection observer for lazy load / fade-in on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animation sequence
  useEffect(() => {
    if (!isVisible) return;

    const timings = [
      800,   // 0 → 1: Start typing
      1800,  // 1 → 2: Task added
      1200,  // 2 → 3: Start moving
      800,   // 3 → 4: Arrive in Done
      1500,  // 4 → 5: Show confirmation
      2000,  // 5 → 0: Reset
    ];

    const timer = setTimeout(() => {
      setStep((prev) => (prev + 1) % 6);
    }, timings[step] || 1000);

    return () => clearTimeout(timer);
  }, [step, isVisible]);

  // Typing animation
  useEffect(() => {
    if (step === 1) {
      setTypedText("");
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index < taskText.length) {
          setTypedText(taskText.slice(0, index + 1));
          index++;
        } else {
          clearInterval(typeInterval);
        }
      }, 80);
      return () => clearInterval(typeInterval);
    }
  }, [step]);

  // Reset typed text when going back to start
  useEffect(() => {
    if (step === 0) {
      setTypedText("");
    }
  }, [step]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
      transition={{ duration: 0.6 }}
      className="relative bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden"
    >
      {/* Microcopy */}
      <div className="px-4 pt-4 pb-2">
        <p className="text-xs text-[#A0A0A0] text-center">
          Here's what it looks like
        </p>
      </div>

      {/* Mini Board */}
      <div className="px-4 pb-4">
        <div className="bg-[#FDFBF7] rounded-xl p-3 min-h-[180px]">
          {/* Board Header */}
          <div className="flex gap-3 mb-3">
            {/* To Do Column */}
            <div className="flex-1">
              <div className="text-xs font-semibold text-[#52525B] mb-2">To <span className="text-[#4CAF50]">Do</span></div>
              <div className="bg-white rounded-lg min-h-[100px] p-2 border border-stone-100">
                {/* Input area - shows during typing */}
                <AnimatePresence>
                  {step === 1 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="bg-stone-50 rounded p-2 mb-2 border border-[#0CBCBF]/30"
                    >
                      <span className="text-xs text-[#27272A]">{typedText}</span>
                      <span className="inline-block w-0.5 h-3 bg-[#0CBCBF] ml-0.5 animate-pulse" />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Task card in To Do */}
                <AnimatePresence>
                  {(step === 2) && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, x: 100 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white rounded-lg p-2 border border-stone-200 shadow-sm"
                    >
                      <div className="flex items-start gap-1.5">
                        <GripVertical size={12} className="text-stone-300 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-[#27272A] leading-tight">{taskText}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Empty state */}
                {(step === 0 || step >= 4) && (
                  <div className="text-xs text-stone-300 text-center py-4">
                    + Add a task
                  </div>
                )}
              </div>
            </div>

            {/* Done Column */}
            <div className="flex-1">
              <div className="text-xs font-semibold text-[#4CAF50] mb-2">Done</div>
              <div className="bg-white rounded-lg min-h-[100px] p-2 border border-stone-100">
                {/* Task moving/arriving in Done */}
                <AnimatePresence>
                  {(step === 3 || step === 4 || step === 5) && (
                    <motion.div
                      initial={step === 3 ? { opacity: 0, x: -50 } : { opacity: 1, x: 0 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="bg-[#E8F5E9] rounded-lg p-2 border border-[#81C784]/30"
                    >
                      <div className="flex items-start gap-1.5">
                        <div className="w-4 h-4 rounded-full bg-[#4CAF50] flex items-center justify-center flex-shrink-0">
                          <Check size={10} className="text-white" />
                        </div>
                        <span className="text-xs text-[#27272A] leading-tight line-through opacity-60">{taskText}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Empty state for Done */}
                {(step === 0 || step === 1 || step === 2) && (
                  <div className="text-xs text-stone-300 text-center py-4">
                    
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Back on track confirmation */}
          <AnimatePresence>
            {step === 5 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center py-2"
              >
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E0F7FA] text-[#0CBCBF] rounded-full text-xs font-medium">
                  <Check size={12} />
                  Back on track
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-1 pb-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`w-1 h-1 rounded-full transition-all duration-300 ${
              i === step ? "bg-[#0CBCBF] w-2" : "bg-stone-200"
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};
